import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CONTACT } from '@/mocks/contactInfo';

const NAV_LINKS = [
  { label: 'Accueil', href: '/' },
  { label: 'Dératisation', href: '/deratisation-guadeloupe', icon: 'ri-bug-line' },
  { label: 'Ravets & cafards', href: '/traitement-ravets-cafards-guadeloupe', icon: 'ri-restaurant-line' },
  { label: 'Termites', href: '/traitement-termites-guadeloupe', icon: 'ri-home-line' },
  {
    label: 'Autres nuisibles',
    children: [
      { label: 'Punaises de lit', href: '/punaises-de-lit-guadeloupe', icon: 'ri-hotel-bed-line' },
      { label: 'Scolopendres', href: '/scolopendres-guadeloupe', icon: 'ri-bug-2-line' },
      { label: 'Moustiques', href: '/demoustication-guadeloupe', icon: 'ri-flashlight-line' },
      { label: 'Désinsectisation', href: '/desinsectisation-guadeloupe', icon: 'ri-shield-check-line' },
    ],
  },
  { label: 'Tarifs', href: '/tarifs', icon: 'ri-price-tag-line' },
  { label: 'Blogue', href: '/blogue', icon: 'ri-article-line' },
  { label: 'Devis', href: '/soumission', icon: 'ri-file-list-line' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const submenuRef = useRef<HTMLDivElement>(null);

  const isActive = (href: string) => location.pathname === href;

  // Scroll detection for desktop nav background
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close everything on route change
  useEffect(() => {
    setSubmenuOpen(false);
    setMobileOpen(false);
    setMobileSubmenuOpen(false);
  }, [location.pathname]);

  // Close desktop submenu on click outside
  useEffect(() => {
    if (!submenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (submenuRef.current && !submenuRef.current.contains(e.target as Node)) {
        setSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [submenuOpen]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 shadow-md" style={{ backgroundColor: '#0B3A2E' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 md:gap-3 shrink-0 cursor-pointer">
              <div className="w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-2xl bg-mango-500 shrink-0">
                <span className="font-heading font-extrabold text-primary-700 text-base md:text-xl">GN</span>
              </div>
              <span className="font-heading font-bold text-white text-sm leading-tight md:text-base xl:text-lg">
                GWADA<br className="hidden sm:block" /><span className="sm:hidden"> </span>NUISIBLES
              </span>
            </Link>

            {/* Desktop Nav — compact on lg, full on xl */}
            <nav className="hidden lg:flex items-center gap-0 xl:gap-1">
              {NAV_LINKS.map((item) => {
                if (item.children) {
                  return (
                    <div
                      key={item.label}
                      ref={submenuRef}
                      className="relative"
                      onMouseEnter={() => setSubmenuOpen(true)}
                      onMouseLeave={() => setSubmenuOpen(false)}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSubmenuOpen(!submenuOpen);
                        }}
                        className="px-2 py-2 text-white/90 hover:text-white text-xs xl:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                        aria-expanded={submenuOpen}
                      >
                        {item.label}
                        <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${submenuOpen ? 'rotate-180' : ''}`}></i>
                      </button>
                      {/* Invisible bridge to prevent mouse gap */}
                      {submenuOpen && (
                        <div className="absolute top-full left-0 w-full h-2.5" />
                      )}
                      {submenuOpen && (
                        <div className="absolute top-[calc(100%+0.625rem)] left-0 bg-white rounded-lg shadow-lg border border-background-200 py-2 min-w-[200px]">
                          {item.children.map((child) => (
                            <Link
                              key={child.href}
                              to={child.href}
                              className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 hover:text-secondary-500 transition-colors whitespace-nowrap"
                            >
                              <i className={`${child.icon} w-4 h-4 flex items-center justify-center text-secondary-500`}></i>
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={`px-2 py-2 text-xs xl:text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      isActive(item.href)
                        ? 'text-white font-semibold'
                        : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop CTA — compact on lg, full on xl */}
            <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
              <a
                href={CONTACT.phoneHref}
                className="text-white/90 hover:text-white text-xs xl:text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1.5"
              >
                <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                <span className="hidden xl:inline">{CONTACT.phone}</span>
                <span className="xl:hidden">{CONTACT.phone}</span>
              </a>
              <a
                href={CONTACT.whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 bg-whatsapp-500 text-white px-3 py-2 xl:px-4 xl:py-2 rounded-lg text-xs xl:text-sm font-semibold whitespace-nowrap hover:bg-whatsapp-600 transition-colors cursor-pointer"
              >
                <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                <span className="hidden xl:inline">WhatsApp</span>
              </a>
            </div>

            {/* Mobile burger */}
            <button
              type="button"
              className="lg:hidden text-white w-10 h-10 flex items-center justify-center cursor-pointer relative z-50"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl transition-transform duration-300`}></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Premium Drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          mobileOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />

        {/* Drawer panel */}
        <div
          className={`absolute top-0 right-0 bottom-0 w-[min(85vw,360px)] bg-primary-800 flex flex-col transition-transform duration-300 ease-out ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 cursor-pointer">
              <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-mango-500">
                <span className="font-heading font-extrabold text-primary-700 text-lg">GN</span>
              </div>
              <span className="font-heading font-bold text-white text-sm leading-tight">
                GWADA<br />NUISIBLES
              </span>
            </Link>
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white cursor-pointer transition-colors"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            {/* Phone CTA */}
            <div className="px-5 py-5">
              <a
                href={CONTACT.phoneHref}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 rounded-xl px-4 py-4 transition-colors cursor-pointer"
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-mango-500 shrink-0">
                  <i className="ri-phone-line text-lg text-primary-700 w-5 h-5 flex items-center justify-center"></i>
                </div>
                <div>
                  <p className="text-white/60 text-xs font-medium uppercase tracking-wider">Appel gratuit</p>
                  <p className="text-white font-bold text-lg whitespace-nowrap">{CONTACT.phone}</p>
                </div>
              </a>
            </div>

            {/* Services grid */}
            <div className="px-5 pb-2">
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Nos services</p>
              <div className="grid grid-cols-2 gap-2">
                {NAV_LINKS.filter((i) => i.href && i.href !== '/' && i.href !== '/tarifs' && i.href !== '/blogue' && i.href !== '/soumission').map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-lg px-3 py-3 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-md bg-secondary-500/20 shrink-0">
                      <i className={`${item.icon} text-sm text-secondary-300 w-4 h-4 flex items-center justify-center`}></i>
                    </div>
                    <span className="text-white/90 text-sm font-medium leading-tight">{item.label}</span>
                  </Link>
                ))}

                {/* Autres nuisibles expandable */}
                <div className="col-span-2">
                  <button
                    type="button"
                    onClick={() => setMobileSubmenuOpen(!mobileSubmenuOpen)}
                    className="w-full flex items-center justify-between bg-white/5 hover:bg-white/10 rounded-lg px-3 py-3 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-500/20 shrink-0">
                        <i className="ri-apps-line text-sm text-accent-300 w-4 h-4 flex items-center justify-center"></i>
                      </div>
                      <span className="text-white/90 text-sm font-medium">Autres nuisibles</span>
                    </div>
                    <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center text-white/60 transition-transform duration-200 ${mobileSubmenuOpen ? 'rotate-180' : ''}`}></i>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${
                      mobileSubmenuOpen ? 'max-h-60 mt-2' : 'max-h-0'
                    }`}
                  >
                    <div className="space-y-1 pl-2">
                      {NAV_LINKS.find((i) => i.children)?.children?.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-white/70 hover:text-white hover:bg-white/5 transition-colors cursor-pointer"
                        >
                          <i className={`${child.icon} text-secondary-400 w-4 h-4 flex items-center justify-center`}></i>
                          <span className="text-sm">{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Secondary links */}
            <div className="px-5 py-4 border-t border-white/10 mt-3">
              <p className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Informations</p>
              <div className="space-y-1">
                {[
                  { label: 'Tarifs', href: '/tarifs', icon: 'ri-price-tag-line' },
                  { label: 'Blogue', href: '/blogue', icon: 'ri-article-line' },
                  { label: 'Devis gratuit', href: '/soumission', icon: 'ri-file-list-line' },
                  { label: 'Accueil', href: '/', icon: 'ri-home-line' },
                ].map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                      isActive(item.href)
                        ? 'bg-white/10 text-white font-semibold'
                        : 'text-white/70 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                    <span className="text-sm">{item.label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Drawer footer — WhatsApp CTA */}
          <div className="px-5 py-4 border-t border-white/10 shrink-0">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-whatsapp-500 hover:bg-whatsapp-600 text-white px-4 py-3.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
              WhatsApp {CONTACT.whatsapp}
            </a>
            <p className="text-white/40 text-xs text-center mt-2">
              Diagnostic gratuit par message
            </p>
          </div>
        </div>
      </div>

      {/* Mobile bottom sticky bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-background-200 shadow-lg">
        <div className="flex items-center gap-2 px-3 py-2.5">
          <a
            href={CONTACT.phoneHref}
            className="flex-1 flex items-center justify-center gap-2 bg-primary-500 text-white px-3 py-3 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
            Appeler
          </a>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 flex items-center justify-center gap-2 bg-whatsapp-500 text-white px-3 py-3 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
            WhatsApp
          </a>
        </div>
      </div>
    </>
  );
}